import React, { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ScrollReveal from "@/components/ui/scroll-reveal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useTranslation } from "@/hooks/useTranslation";

const Testimonials = () => {
  const { t } = useTranslation();

  const testimonials = [
    {
      quote: t("testimonials.testimonial1.quote"),
      author: t("testimonials.testimonial1.author"),
      role: t("testimonials.testimonial1.role"),
      company: t("testimonials.testimonial1.company"),
      rating: 5,
      image: "/Joakim_Rounded.webp"
    },
    {
      quote: t("testimonials.testimonial2.quote"),
      author: t("testimonials.testimonial2.author"),
      role: t("testimonials.testimonial2.role"),
      company: t("testimonials.testimonial2.company"),
      rating: 5,
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=88&h=88&q=80"
    },
    {
      quote: t("testimonials.testimonial3.quote"),
      author: t("testimonials.testimonial3.author"),
      role: t("testimonials.testimonial3.role"),
      company: t("testimonials.testimonial3.company"),
      rating: 5,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=88&h=88&q=80"
    },
    {
      quote: t("testimonials.testimonial4.quote"),
      author: t("testimonials.testimonial4.author"),
      role: t("testimonials.testimonial4.role"),
      company: t("testimonials.testimonial4.company"),
      rating: 5,
      image: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=88&h=88&q=80"
    },
  ];

  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [api, setApi] = useState<any>(null);

  useEffect(() => {
    if (!api || !isAutoPlaying) return;
    
    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [api, isAutoPlaying]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-10 left-10 w-32 sm:w-64 h-32 sm:h-64 bg-brand-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-40 sm:w-80 h-40 sm:h-80 bg-amber-500/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <ScrollReveal direction="up" duration={700}>
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
            <span className="inline-block text-brand-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 md:mb-3">
              {t("testimonials.trustedBy")}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 text-foreground">
              {t("testimonials.title")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground px-2">
              {t("testimonials.subtitle")}
            </p>
          </div>
        </ScrollReveal>

        <div 
          className="relative px-2 sm:px-0" 
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Carousel
            setApi={setApi}
            className="w-full max-w-6xl mx-auto"
            opts={{
              align: "start",
              loop: true,
            }}
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <div className="h-full">
                    <div className="bg-card p-4 sm:p-6 md:p-8 rounded-2xl shadow-lg border border-border/50 h-full flex flex-col relative group hover:shadow-xl hover:border-brand-200/50 transition-all duration-300">
                      {/* Quote icon */}
                      <Quote className="absolute top-3 right-3 sm:top-4 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 text-brand-500/10 group-hover:text-brand-500/20 transition-colors" />
                      
                      <div className="flex mb-3 md:mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className="text-amber-400 fill-amber-400"
                          />
                        ))}
                      </div>
                      <blockquote className="text-sm sm:text-base text-foreground/80 mb-4 md:mb-6 flex-grow leading-relaxed">
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="flex items-center mt-auto pt-3 md:pt-4 border-t border-border/50">
                        <Avatar className="h-10 w-10 sm:h-12 sm:w-12 mr-3 sm:mr-4 border-2 border-brand-200 ring-2 ring-brand-100">
                          <AvatarImage 
                            src={testimonial.image} 
                            alt={testimonial.author}
                            loading="lazy"
                            decoding="async"
                          />
                          <AvatarFallback className="bg-brand-100 text-brand-700 text-xs sm:text-sm">{testimonial.author.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground text-sm sm:text-base truncate">{testimonial.author}</p>
                          <p className="text-muted-foreground text-xs sm:text-sm truncate">
                            {testimonial.role}, {testimonial.company}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="absolute -left-4 lg:-left-12 border-border bg-card text-foreground hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200" />
              <CarouselNext className="absolute -right-4 lg:-right-12 border-border bg-card text-foreground hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200" />
            </div>
          </Carousel>
          
          {/* Mobile swipe indicator */}
          <div className="flex justify-center gap-2 mt-4 md:hidden">
            {testimonials.map((_, index) => (
              <div 
                key={index} 
                className="w-2 h-2 rounded-full bg-brand-200 dark:bg-brand-800"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
